import React, { useEffect, useRef } from "react";
import { siteConfig } from "../../config/siteConfig";

const PremiumModal = ({ isOpen, onClose, onGoToBilling }) => {
  const panelRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    // Guardar el foco previo para restaurarlo al cerrar
    previouslyFocusedRef.current = document.activeElement;

    // Llevar el foco al panel del modal
    if (panelRef.current) panelRef.current.focus();

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      // Focus trap básico: mantener la tabulación dentro del modal
      if (e.key === "Tab" && panelRef.current) {
        const focusables = panelRef.current.querySelectorAll(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      // Restaurar el foco al elemento que abrió el modal
      if (previouslyFocusedRef.current && previouslyFocusedRef.current.focus) {
        previouslyFocusedRef.current.focus();
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Copy centralizable por nicho (con fallback generico de la plantilla)
  const premiumCopy = siteConfig.uiCopy?.premiumModal || {};
  const icon = premiumCopy.icon || "🔒";
  const title = premiumCopy.title || "Premium-ominaisuus";
  const body =
    premiumCopy.body ||
    "Harjoituskoe ja aihekohtaiset harjoitukset ovat vain PRO-jäsenille. Valmistaudu kokeeseen kunnolla!";
  const ctaPrimary = premiumCopy.ctaPrimary || "Katso Premium-paketti";
  const ctaDismiss = premiumCopy.ctaDismiss || "Ehkä myöhemmin";

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      {/* Overlay con desenfoque */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-fadeIn"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Contenido del Modal */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="premium-modal-title"
        tabIndex={-1}
        className="relative bg-white w-full max-w-sm rounded-3xl p-8 shadow-2xl animate-scaleIn text-center"
      >
        <div
          className="w-20 h-20 bg-cyan-100 text-cyan-600 rounded-full flex items-center justify-center text-4xl mx-auto mb-6"
          aria-hidden="true"
        >
          {icon}
        </div>

        <h2
          id="premium-modal-title"
          className="text-2xl font-black text-slate-800 mb-2"
        >
          {title}
        </h2>

        <p className="text-slate-600 mb-8 leading-relaxed">{body}</p>

        <div className="flex flex-col gap-3">
          <button
            onClick={onGoToBilling}
            className="w-full py-4 bg-teal-700 text-white rounded-2xl font-black hover:bg-teal-800 transition-all shadow-lg shadow-teal-200"
          >
            {ctaPrimary}
          </button>

          <button
            onClick={onClose}
            className="w-full py-3 text-slate-400 font-bold hover:text-slate-600 transition-all"
          >
            {ctaDismiss}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PremiumModal;
